import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'manager' | 'partner';
  commission_rate?: number;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, userData: Partial<User>) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session with error handling
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('❌ Error getting session:', error);
          setLoading(false);
          return;
        }
        
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchUserProfile(session.user);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state changed:', event, session?.user?.email);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchUserProfile(session.user);
        } else {
          setUserProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (authUser: SupabaseUser) => {
    try {
      setLoading(true);
      console.log('👤 Fetching profile for user:', authUser.email, authUser.id);

      // Tạo profile mặc định để tránh lỗi RLS
      const defaultProfile: User = {
        id: authUser.id,
        auth_user_id: authUser.id,
        email: authUser.email || '',
        full_name: authUser.email?.split('@')[0] || 'User',
        role: authUser.email === 'quangdoanh.plus84@gmail.com' ? 'manager' : 'partner',
        commission_rate: 15,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Thử query với timeout để tránh infinite recursion
      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('email', authUser.email)
        .maybeSingle();

      // Timeout sau 3 giây
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout')), 3000)
      );

      try {
        const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;
        
        if (!error && data) {
          console.log('✅ Found user profile:', data);
          setUserProfile(data);
        } else {
          console.log('⚠️ No profile found or error, using default:', error?.message);
          setUserProfile(defaultProfile);
        }
      } catch (queryError) {
        console.log('❌ Query failed, using default profile:', queryError);
        setUserProfile(defaultProfile);
      }

    } catch (error) {
      console.error('❌ Error in fetchUserProfile:', error);
      // Fallback: Tạo profile mặc định
      const fallbackProfile: User = {
        id: authUser.id,
        auth_user_id: authUser.id,
        email: authUser.email || '',
        full_name: authUser.email?.split('@')[0] || 'User',
        role: authUser.email === 'quangdoanh.plus84@gmail.com' ? 'manager' : 'partner',
        commission_rate: 15,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUserProfile(fallbackProfile);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔑 Attempting sign in for:', email);
      
      // Kiểm tra kết nối mạng trước
      if (!navigator.onLine) {
        return { error: { message: 'Không có kết nối mạng. Vui lòng kiểm tra kết nối internet.' } };
      }

      // Thêm timeout cho request đăng nhập
      const signInPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: Kết nối quá chậm')), 10000)
      );

      const { error } = await Promise.race([signInPromise, timeoutPromise]) as any;
      
      if (error) {
        console.error('❌ Sign in error:', error);
        // Xử lý các loại lỗi khác nhau
        if (error.message?.includes('Invalid login credentials')) {
          return { error: { message: 'Email hoặc mật khẩu không đúng' } };
        } else if (error.message?.includes('Email not confirmed')) {
          return { error: { message: 'Vui lòng xác nhận email trước khi đăng nhập' } };
        } else if (error.message?.includes('Failed to fetch') || error.name === 'AuthRetryableFetchError') {
          return { error: { message: 'Lỗi kết nối. Vui lòng thử lại sau.' } };
        }
        return { error: { message: error.message || 'Đăng nhập thất bại' } };
      } else {
        console.log('✅ Sign in successful');
        return { error: null };
      }
      
    } catch (error: any) {
      console.error('❌ Sign in exception:', error);
      if (error.message?.includes('Timeout')) {
        return { error: { message: 'Kết nối quá chậm. Vui lòng thử lại.' } };
      }
      return { error: { message: 'Lỗi kết nối. Vui lòng kiểm tra internet và thử lại.' } };
    }
  };

  const signUp = async (email: string, password: string, userData: Partial<User>) => {
    try {
      // Kiểm tra kết nối mạng
      if (!navigator.onLine) {
        return { error: { message: 'Không có kết nối mạng. Vui lòng kiểm tra kết nối internet.' } };
      }

      // Tạo tài khoản không cần xác thực email
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: undefined // Bỏ xác thực email
        }
      });

      if (!error && data.user) {
        // Tạo profile trong database với trạng thái pending cho partner
        try {
          const { error: profileError } = await supabase
            .from('users')
            .insert({
              auth_user_id: data.user.id,
              email,
              full_name: userData.full_name || email.split('@')[0],
              role: userData.role || 'partner',
              commission_rate: userData.commission_rate || 15,
            });

          if (profileError) {
            console.error('Error creating user profile:', profileError);
          }

          // Tạo partner record với trạng thái pending
          if ((userData.role || 'partner') === 'partner') {
            const { error: partnerError } = await supabase
              .from('partners')
              .insert({
                id: data.user.id,
                partner_name: userData.full_name || email.split('@')[0],
                email,
                commission_rate: userData.commission_rate || 15,
                is_active: false, // Đang chờ phê duyệt
                status: 'pending', // Trạng thái chờ phê duyệt
                total_orders: 0,
                total_revenue: 0,
                total_commission: 0,
                this_week_revenue: 0,
                this_month_revenue: 0,
                this_year_revenue: 0,
                last_order_date: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });

            if (partnerError) {
              console.error('Error creating partner record:', partnerError);
            }
          }
        } catch (profileError) {
          console.error('Exception creating user profile:', profileError);
        }
      }

      return { error };
    } catch (error: any) {
      console.error('❌ Sign up exception:', error);
      return { error: { message: 'Lỗi kết nối. Vui lòng thử lại.' } };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUserProfile(null);
    } catch (error) {
      console.error('❌ Sign out error:', error);
      // Vẫn clear local state ngay cả khi có lỗi
      setUser(null);
      setUserProfile(null);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
